import { useEffect, useMemo, useState } from 'react'
import type {
  FormTemplateDetail,
  FormTemplateField,
  FormTemplateSection,
  FormTemplateVersionDetail,
  FormFieldUpdatePayload,
  FormSectionUpdatePayload,
} from '@/types'
import { FormVersionStatus, FormType } from '@/types'
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import SectionTree from './SectionTree'
import FieldCanvas from './FieldCanvas'
import FieldInspector from './FieldInspector'
import LogicRulePanel from './LogicRulePanel'
import type {
  CreateLogicRulePayload,
  UpdateLogicRulePayload,
} from '@/services/api/formTemplates'
import { fieldMatchesKeyword, normalizeKeyword } from './fieldFilters'

interface FormTemplateEditorProps {
  template?: FormTemplateDetail | null
  activeVersionId?: string
  onChangeVersion: (versionId: string) => void
  onCreateSection: (templateId: string, versionId: string, title: string) => Promise<void>
  onUpdateSection: (
    templateId: string,
    versionId: string,
    sectionId: string,
    payload: FormSectionUpdatePayload,
  ) => Promise<void>
  onDeleteSection: (templateId: string, versionId: string, sectionId: string) => Promise<void>
  onCreateField: (templateId: string, versionId: string, sectionId: string) => Promise<void>
  onRenameField: (
    templateId: string,
    versionId: string,
    fieldId: string,
    label: string,
  ) => Promise<void>
  onUpdateField: (
    templateId: string,
    versionId: string,
    fieldId: string,
    payload: FormFieldUpdatePayload,
  ) => Promise<void>
  onDeleteField: (templateId: string, versionId: string, fieldId: string) => Promise<void>
  onReorderFields: (
    templateId: string,
    versionId: string,
    sectionId: string,
    orderedIds: string[],
  ) => Promise<void>
  focusFieldKey?: string
  onFocusFieldHandled?: () => void
  onCreateLogicRule: (templateId: string, versionId: string, payload: CreateLogicRulePayload) => Promise<void>
  onUpdateLogicRule: (
    templateId: string,
    versionId: string,
    ruleId: string,
    payload: UpdateLogicRulePayload,
  ) => Promise<void>
  onDeleteLogicRule: (templateId: string, versionId: string, ruleId: string) => Promise<void>
  onReorderLogicRules: (
    templateId: string,
    versionId: string,
    orderedRuleIds: string[],
  ) => Promise<void>
  onOpenTemplateSettings?: () => void
  onOpenCreateVersion?: () => void
  onOpenPublishVersion?: () => void
  canPublishActiveVersion?: boolean
}

const fallbackVersion: FormTemplateVersionDetail = {
  id: '',
  versionNumber: 1,
  status: FormVersionStatus.DRAFT,
  sections: [],
  fields: [],
  logicRules: [],
}

export const FormTemplateEditor = ({
  template,
  activeVersionId,
  onChangeVersion,
  onCreateSection,
  onUpdateSection,
  onDeleteSection,
  onCreateField,
  onRenameField,
  onUpdateField,
  onDeleteField,
  onReorderFields,
  focusFieldKey,
  onFocusFieldHandled,
  onCreateLogicRule,
  onUpdateLogicRule,
  onDeleteLogicRule,
  onReorderLogicRules,
  onOpenTemplateSettings,
  onOpenCreateVersion,
  onOpenPublishVersion,
  canPublishActiveVersion = false,
}: FormTemplateEditorProps) => {
  const [selectedSectionId, setSelectedSectionId] = useState<string | undefined>()
  const [selectedFieldId, setSelectedFieldId] = useState<string | undefined>()
  const [fieldFilter, setFieldFilter] = useState('')
  const [detailTab, setDetailTab] = useState<'properties' | 'logic'>('properties')

  const activeVersion = useMemo(() => {
    if (!template) return fallbackVersion
    if (!template.versions || template.versions.length === 0) return fallbackVersion
    const found = template.versions.find((version) => version.id === activeVersionId)
    return found || template.versions[0]
  }, [template, activeVersionId])

  const sections: FormTemplateSection[] = useMemo(
    () => activeVersion.sections ?? [],
    [activeVersion.sections],
  )
  const fields: FormTemplateField[] = useMemo(
    () => activeVersion.fields ?? [],
    [activeVersion.fields],
  )
  const logicRules = useMemo(() => activeVersion.logicRules ?? [], [activeVersion.logicRules])
  const categoryOptions = useMemo(() => {
    const categories = new Set<string>()
    fields.forEach(fieldItem => {
      const category = fieldItem.category?.trim()
      if (category) {
        categories.add(category)
      }
    })
    return Array.from(categories).sort((a, b) => a.localeCompare(b, 'zh-Hant-TW', { numeric: true }))
  }, [fields])

  const subcategoryOptions = useMemo(() => {
    const map = new Map<string, Set<string>>()
    fields.forEach(fieldItem => {
      const category = fieldItem.category?.trim()
      const subcategory = fieldItem.subcategory?.trim()
      if (!category || !subcategory) return
      if (!map.has(category)) {
        map.set(category, new Set<string>())
      }
      map.get(category)!.add(subcategory)
    })

    const record: Record<string, string[]> = {}
    map.forEach((set, category) => {
      record[category] = Array.from(set).sort((a, b) => a.localeCompare(b, 'zh-Hant-TW', { numeric: true }))
    })
    return record
  }, [fields])
  const templateId = template?.id ?? null

  useEffect(() => {
    if (!focusFieldKey) return
    const targetField = fields.find(field => field.key === focusFieldKey)
    if (!targetField) return

    if (targetField.sectionId) {
      setSelectedSectionId(targetField.sectionId)
    }
    setSelectedFieldId(targetField.id)
    onFocusFieldHandled?.()
  }, [focusFieldKey, fields, onFocusFieldHandled])

  useEffect(() => {
    if (!templateId) {
      setSelectedSectionId(undefined)
      setSelectedFieldId(undefined)
      return
    }
    if (!selectedSectionId && sections.length > 0) {
      setSelectedSectionId(sections[0].id)
    }
  }, [templateId, activeVersion.id, sections, selectedSectionId])

  useEffect(() => {
    if (selectedFieldId) {
      const stillExists = fields.some(field => field.id === selectedFieldId)
      if (!stillExists) {
        setSelectedFieldId(undefined)
      }
    }
  }, [fields, selectedFieldId])

  const normalizedFilter = useMemo(() => normalizeKeyword(fieldFilter), [fieldFilter])

  const sectionMatchCounts = useMemo(() => {
    if (!normalizedFilter) return {}
    const counts: Record<string, number> = {}
    fields.forEach(field => {
      if (!field.sectionId) return
      if (fieldMatchesKeyword(field, normalizedFilter)) {
        counts[field.sectionId] = (counts[field.sectionId] ?? 0) + 1
      }
    })
    return counts
  }, [fields, normalizedFilter])

  useEffect(() => {
    if (!selectedSectionId) {
      setSelectedFieldId(undefined)
      return
    }

    const sectionFields = fields
      .filter(field => field.sectionId === selectedSectionId)
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))

    if (sectionFields.length === 0) {
      setSelectedFieldId(undefined)
      return
    }

    const visibleFields = sectionFields.filter(field =>
      !normalizedFilter || fieldMatchesKeyword(field, normalizedFilter),
    )

    if (visibleFields.length === 0) {
      setSelectedFieldId(undefined)
      return
    }

    if (!selectedFieldId || !visibleFields.some(field => field.id === selectedFieldId)) {
      setSelectedFieldId(visibleFields[0].id)
    }
  }, [selectedSectionId, fields, selectedFieldId, normalizedFilter])

  const handleAddSection = async () => {
    if (!template) return
    const title = window.prompt('請輸入節點名稱：', '未命名節點')
    if (!title) return
    await onCreateSection(template.id, activeVersion.id, title)
  }

  const handleRenameSection = async (sectionId: string, title: string) => {
    if (!template) return
    await onUpdateSection(template.id, activeVersion.id, sectionId, { title })
  }

  const handleDeleteSection = async (section: FormTemplateSection) => {
    if (!template) return
    const confirmed = window.confirm(`確定要刪除「${section.title || '未命名節點'}」嗎？此動作將移除節點及其欄位。`)
    if (!confirmed) {
      return
    }
    await onDeleteSection(template.id, activeVersion.id, section.id)
    if (selectedSectionId === section.id) {
      setSelectedSectionId(undefined)
      setSelectedFieldId(undefined)
    }
  }

  const handleRenameFieldInline = async (fieldId: string, label: string) => {
    if (!template) return
    await onRenameField(template.id, activeVersion.id, fieldId, label)
  }

  const handleAddField = async (sectionId: string) => {
    if (!template) return
    await onCreateField(template.id, activeVersion.id, sectionId)
    setSelectedSectionId(sectionId)
  }

  const handleUpdateField = async (fieldId: string, payload: FormFieldUpdatePayload) => {
    if (!template) return
    await onUpdateField(template.id, activeVersion.id, fieldId, payload)
  }

  const handleDeleteField = async (fieldId: string) => {
    if (!template) return
    await onDeleteField(template.id, activeVersion.id, fieldId)
    setSelectedFieldId(undefined)
  }

  const handleReorderFields = async (sectionId: string, orderedIds: string[]) => {
    if (!template) return
    await onReorderFields(template.id, activeVersion.id, sectionId, orderedIds)
  }

  const handleCreateLogicRule = async (payload: CreateLogicRulePayload) => {
    if (!template) return
    await onCreateLogicRule(template.id, activeVersion.id, payload)
  }

  const handleUpdateLogicRule = async (ruleId: string, payload: UpdateLogicRulePayload) => {
    if (!template) return
    await onUpdateLogicRule(template.id, activeVersion.id, ruleId, payload)
  }

  const handleDeleteLogicRule = async (ruleId: string) => {
    if (!template) return
    await onDeleteLogicRule(template.id, activeVersion.id, ruleId)
  }

  const handleReorderLogicRules = async (orderedRuleIds: string[]) => {
    if (!template) return
    await onReorderLogicRules(template.id, activeVersion.id, orderedRuleIds)
  }

  const selectedSection = sections.find(section => section.id === selectedSectionId)
  const selectedField = fields.find(field => field.id === selectedFieldId)

  return (
    <div className="flex h-full flex-col bg-gray-50">
      <div className="border-b border-gray-200 bg-white px-4 py-3">
        <div className="flex items-center space-x-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{template?.name ?? '未選取模板'}</h2>
            {template?.latestVersion?.description && (
              <p className="text-sm text-gray-500">{template.latestVersion.description}</p>
            )}
          </div>
          {template && (
            <div className="ml-auto flex flex-wrap items-center gap-2 text-sm">
              <label htmlFor="version-select" className="text-gray-500">
                版本
              </label>
              <select
                id="version-select"
                value={activeVersion.id}
                onChange={(e) => onChangeVersion(e.target.value)}
                className="rounded-md border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500"
              >
                {template.versions.map(version => (
                  <option key={version.id} value={version.id}>
                    v{version.versionNumber} · {version.status}
                  </option>
                ))}
              </select>
              {onOpenCreateVersion && (
                <button
                  type="button"
                  onClick={onOpenCreateVersion}
                  className="rounded border border-primary-500 px-2 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50"
                >
                  新增版本
                </button>
              )}
              {onOpenPublishVersion && (
                <button
                  type="button"
                  onClick={onOpenPublishVersion}
                  className="rounded border border-primary-500 px-2 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50 disabled:opacity-60"
                  disabled={!canPublishActiveVersion}
                >
                  發佈版本
                </button>
              )}
              {onOpenTemplateSettings && (
                <button
                  type="button"
                  onClick={onOpenTemplateSettings}
                  className="rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100"
                >
                  模板設定
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-4 p-4 lg:flex-row">
        <div className="lg:w-72">
          <SectionTree
            sections={sections}
            selectedSectionId={selectedSectionId}
            onSelect={setSelectedSectionId}
            onAddSection={handleAddSection}
            onRenameSection={handleRenameSection}
            onDeleteSection={handleDeleteSection}
            filterKeyword={fieldFilter}
            matchCounts={sectionMatchCounts}
          />
        </div>

        <div className="flex-1 min-w-0 flex flex-col">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <div className="relative w-full sm:w-72">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input
                type="search"
                value={fieldFilter}
                onChange={(e) => setFieldFilter(e.target.value)}
                placeholder="搜尋欄位名稱、描述或選項"
                className="w-full rounded-md border border-gray-300 pl-8 pr-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
              />
            </div>
            {fieldFilter.trim().length > 0 && (
              <button
                type="button"
                onClick={() => setFieldFilter('')}
                className="rounded border border-gray-300 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100"
              >
                清除搜尋
              </button>
            )}
          </div>
          <FieldCanvas
            section={selectedSection}
            fields={fields}
            selectedFieldId={selectedFieldId}
            onSelectField={setSelectedFieldId}
            onAddField={handleAddField}
            onRenameField={handleRenameFieldInline}
            onReorderFields={handleReorderFields}
            filterKeyword={normalizedFilter ? fieldFilter : undefined}
          />
        </div>

        <div className="lg:w-80 flex h-full min-h-0 flex-col gap-3">
          <div className="flex gap-1 rounded-md bg-white p-1 shadow-sm border border-gray-200">
            <button
              type="button"
              onClick={() => setDetailTab('properties')}
              className={`flex-1 rounded-md px-3 py-1.5 text-sm ${
                detailTab === 'properties'
                  ? 'bg-primary-600 text-white shadow'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              欄位屬性
            </button>
            <button
              type="button"
              onClick={() => setDetailTab('logic')}
              className={`flex-1 rounded-md px-3 py-1.5 text-sm ${
                detailTab === 'logic'
                  ? 'bg-primary-600 text-white shadow'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              條件邏輯
            </button>
          </div>
          <div className="flex-1 min-h-0">
            {detailTab === 'properties' ? (
              <FieldInspector
                field={selectedField}
                onUpdateField={handleUpdateField}
                onDeleteField={handleDeleteField}
                availableCategories={categoryOptions}
                availableSubcategories={subcategoryOptions}
                formType={template?.formType ?? FormType.PERIODIC}
              />
            ) : (
              <LogicRulePanel
                rules={logicRules}
                fields={fields}
                filterKeyword={fieldFilter}
                onCreateRule={handleCreateLogicRule}
                onUpdateRule={handleUpdateLogicRule}
                onDeleteRule={handleDeleteLogicRule}
                onReorderRules={handleReorderLogicRules}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default FormTemplateEditor
